<template>
  <div id="chart" :style="`width: ${width}px`">
    <apexchart
      v-if="sensores"
      ref="chart"
      type="line"
      :height="`${height}px` || '350vh'"
      :options="chartOptions"
      :series="series"
    />
    <div v-else style="text-align: left">
      <md-progress-spinner
        :md-diameter="50"
        :md-stroke="5"
        md-mode="indeterminate"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment'

import { parseDate, parseDateToMinutos } from '@/utils/transform'
import {
  registerChannelListener,
  removeChannelListener,
} from '@/utils/web-socket'

export default {
  name: 'ViewGraphicArea',
  props: [
    'sensores',
    'tipo',
    'range',
    'maxValue',
    'height',
    'width',
    'periodo',
  ],
  data() {
    return {
      refsToListener: [],
      timeOffset: new Date().getTimezoneOffset() * 1000 * 60,
      series: [],
      clearData: null,
      chartOptions: {
        colors: [
          '#008FFB',
          '#00E396',
          '#FEB019',
          '#FF4560',
          '#775DD0',
          '#966e23',
          '#ff07dd',
          '#4af0ff',
          '#000dff',
          '#3f3f3f',
        ],
        markers: {
          size: 0,
        },
        chart: {
          type: 'line',
          zoom: {
            enabled: false,
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          width: 2,
          curve: 'straight',
        },
        title: {
          text: `${this.tipo}`,
          align: 'left',
        },
        grid: {
          row: {
            colors: ['#f3f3f3', 'transparent'], // takes an array which will be repeated on columns
            opacity: 0.5,
          },
        },
        xaxis: {
          type: 'datetime',
          labels: {
            formatter: function (value, timestamp) {
              return moment(new Date(timestamp)).format('HH:mm')
            },
          },
          max: this.maxValue || new Date().getTime(),
          range: this.range || 1000 * 60 * 60 * 2,
        },
        yaxis: {
          labels: {
            formatter: function (val) {
              if (val === null || val === undefined) {
                return val
              }

              return val.toFixed(2)
            },
          },
          tickAmount: 5,
          min: 0,
          max: (x) => x * 1.1,
        },
      },
    }
  },
  beforeMount() {
    this.init()

    if (this.periodo) {
      this.chartOptions.xaxis.range =
        1000 * 60 * parseDateToMinutos(this.periodo)
    }

    const [horas] = parseDate(this.periodo)

    if (horas <= 8) {
      this.chartOptions.markers.size = 4
    }

    this.interval = setInterval(() => {
      this.chartOptions.xaxis.max = new Date().getTime()
      this.chartOptions = { ...this.chartOptions }
    }, 5000)

    // Prevent Memory Leak
    this.clearData = setInterval(() => {
      this.clearSeriesData()

      // 30 minutes
    }, 1000 * 60 * 30)
  },
  beforeDestroy() {
    this.refsToListener.forEach((e) => {
      const [ref, type, id] = e

      if (type === 'sensor') {
        removeChannelListener(
          'view_sensor_id:' + id,
          'sensor:update_value',
          ref
        )
      }

      if (type === 'virtual') {
        removeChannelListener('sensor_virtual_id:' + id, 'update_value', ref)
      }
    })

    clearInterval(this.interval)
    clearInterval(this.clearData)
  },
  methods: {
    async init() {
      if (!this.sensores) {
        return
      }

      this.sensores.forEach((e) => {
        this.series.push({
          id: e.sensor.id,
          name: `${e.sensor.descricao}`,
          data: e.dados,
        })
      })

      await this.beginListenSensores()
    },
    // TODO: add method to prevent memory leak
    async beginListenSensores() {
      for (const e of this.sensores) {
        let ref

        if (e.type === 'sensor') {
          ref = await registerChannelListener(
            'view_sensor_id:' + e.sensor.id,
            'sensor:update_value',
            (payload) => {
              const series = this.series.find((x) => x.id === e.sensor.id)

              if (series) {
                series.data = [
                  [new Date(payload.horarioColeta).getTime(), payload.valor],
                  ...series.data,
                ]
                this.series = [...this.series]
              }
            }
          )
        }

        if (e.type === 'virtual') {
          ref = await registerChannelListener(
            'sensor_virtual_id:' + e.sensor.id,
            'update_value',
            (payload) => {
              const series = this.series.find((x) => x.id === e.sensor.id)
              if (series) {
                series.data = [
                  [new Date(payload.horario).getTime(), payload.result],
                  ...series.data,
                ]
                this.series = [...this.series]
              }
            }
          )
        }

        this.refsToListener.push([ref, e.type, e.sensor.id])
      }
    },
    getFilterMoment() {
      return moment()
        .subtract(parseDateToMinutos(this.periodo) || 1, 'minute')
        .valueOf()
    },
    clearSeriesData() {
      const filterMoment = this.getFilterMoment()

      this.chartOptions = {
        ...this.chartOptions,
        chart: {
          ...this.chartOptions.chart,
          animations: {},
        },
      }

      this.series = this.series.map((e) => {
        e.data = e.data.filter((x) => {
          return x[0] > filterMoment
        })
        return e
      })
    },
  },
}
</script>

<style scoped></style>
